import React, {
  Fragment,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';

import { twMerge } from 'tailwind-merge';

import type { HTMLProps } from 'react';

import { Combobox, Transition } from '@headlessui/react';

import Icon from 'components/icon/icon';
import Typography from 'components/typography/typography';

export type Option = {
  label: string | number;
  value: string | number;
} | null;
export interface AutocompletePropTypes extends HTMLProps<HTMLInputElement> {
  options: Option[];
  autoCompleteInputClassName?: string;
  OptionsContainerClassName?: string;
  optionClassName?: string;
  isLoading?: boolean;
  defaultOption?: Option;
  emptyResultMessage?: string;
  animated?: boolean;
  onValueChange?: (value: string) => void;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  asynch?: boolean;
}

const Autocomplete: React.FC<AutocompletePropTypes> = (props) => {
  const {
    options,
    onValueChange,
    onInputChange,
    autoCompleteInputClassName,
    OptionsContainerClassName,
    optionClassName,
    defaultOption,
    isLoading,
    emptyResultMessage,
    animated,
    asynch,
    placeholder,
    ...inputFieldProps
  } = props;
  const [filteredOptions, setFilteredOptions] = useReducer(
    (prevOptions: Option[], Options: Option[]) => {
      return Options;
    },
    options,
  );

  const [selectedOption, setSelectedOption] = useReducer(
    (prevOption: Option, newOption: Option) => {
      return newOption;
    },
    null as unknown as Option,
  );

  const [value, setValue] = useState(null as unknown as string);
  const [inputValue, setInputValue] = useState('');

  const handleValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
      onValueChange?.(event.target.value);
    },
    [setValue, onValueChange],
  );
  const handleChange = useCallback(
    (option: Option) => {
      setSelectedOption(option);
      onValueChange?.(option?.value.toString() || '');
      setValue(option?.value.toString() || '');
    },
    [onValueChange, setSelectedOption],
  );

  useEffect(() => {
    if (asynch) {
      setFilteredOptions(options);
      if (options.length === 0 && !isLoading)
        setSelectedOption(null as unknown as Option);
    }
    // default Option is used when the filter is saved in local storage.
    if (defaultOption) setSelectedOption(defaultOption);
    // eslint-disable-next-line
  }, [options]);

  const filterOptions = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!asynch) {
        if (event?.target?.value === '') {
          onInputChange?.(event);
        } else {
          const filteredlist = options.filter((option) => {
            return option?.label
              .toString()
              .toLowerCase()
              .includes(event?.target?.value.toLowerCase());
          });
          setFilteredOptions(filteredlist);
        }
      } else {
        onInputChange?.(event);
        setFilteredOptions(options);
        setInputValue(event.target.value);
      }
    },
    // eslint-disable-next-line
    [setFilteredOptions, options],
  );

  const inputClassName = twMerge(
    'pl-10 border border-1 border-gray300',
    'bg-white h-10 w-full rounded-lg pr-8',
    'text-gray900',
    autoCompleteInputClassName,
  );

  const optionsAreaClassNames = twMerge(
    'absolute z-20 top-2 left-0',
    'border border-1 border-gray300',
    'bg-white w-full rounded-lg mt-3 py-2',
    'max-h-72 overflow-scroll',
    OptionsContainerClassName,
  );

  const comboboxOptionClassName = twMerge(
    'relative cursor-default select-none py-2 pl-10 pr-4 h-fit',
    ' whitespace-nowrap text-gray800',
    optionClassName,
  );

  return (
    <div className="relative w-full">
      <Combobox value={selectedOption} onChange={handleChange} nullable>
        <div className="relative w-full h-fit">
          <Icon
            icon="search"
            className="absolute top-1/2 left-3 transform -translate-y-1/2"
            size={20}
          />
          <Combobox.Input
            onChange={filterOptions}
            className={inputClassName}
            displayValue={(option?: Option) => option?.label.toString() || ''}
            placeholder={placeholder}
          />
        </div>

        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
          <div className="absolute top-[1/2] right-2 text-gray-400">
            <Icon icon="arrow_down" />
          </div>
        </Combobox.Button>
        <Transition
          as={Fragment}
          enter={animated ? 'transition duration-300 ease-out' : ''}
          enterFrom={animated ? 'transform scale-50 opacity-20' : ''}
          enterTo={animated ? 'transform scale-100 opacity-100' : ''}
          leave={animated ? 'transition duration-300 ease-out' : ''}
          leaveFrom={animated ? 'transform scale-100 opacity-100' : ''}
          leaveTo={animated ? 'transform scale-50 opacity-20' : ''}
        >
          <div className="relative w-full mx-0 z-30">
            {filteredOptions.length ? (
              <Combobox.Options className={optionsAreaClassNames}>
                {filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option?.value}
                    value={option}
                    className={({ active }) =>
                      ` ${comboboxOptionClassName} ${
                        active ? 'bg-gray300 text-white' : 'text-gray800'
                      }`
                    }
                  >
                    {({ selected }) => (
                      <div className="flex justify-start items-center w-full">
                        <Typography
                          className="whitespace-normal flex justify-start items-center text-left"
                          as="h5"
                          variant={
                            selected ? 'body-medium-bold' : 'body-medium'
                          }
                        >
                          {option?.label}
                        </Typography>

                        {selected ? (
                          <Icon
                            icon="selected"
                            className="absolute top-3 left-3 text-mediumGreen"
                            size={16}
                          />
                        ) : null}
                      </div>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            ) : (
              !isLoading &&
              inputValue.length >= 4 && (
                <div className={optionsAreaClassNames}>
                  <Typography
                    as="h3"
                    variant="body-medium-bold"
                    className="flex my-2 justify-center items-center"
                  >
                    {emptyResultMessage}
                  </Typography>
                </div>
              )
            )}
          </div>
        </Transition>
        {
          // this input field is used for validation and errors
        }
        <input
          type="hidden"
          value={value || ''}
          onChange={handleValueChange}
          {...inputFieldProps}
        />
      </Combobox>
    </div>
  );
};

export default Autocomplete;
